import type { NormalizedHoroscope, NormalizedHoroscopeItem, PalaceId } from './domain.js';

export interface ContractIssue {
  path: string;
  code: string;
  message: string;
}

const PALACE_IDS: readonly PalaceId[] = [
  'life','siblings','spouse','children','wealth','health','travel','friends','career','property','wellbeing','parents',
] as const;
const MOVING_SCOPES = ['decadal','yearly','monthly','daily','hourly'] as const;

function validateItem(item: NormalizedHoroscopeItem, issues: ContractIssue[]): void {
  const base = `horoscope.${item.scope}`;
  if (!Number.isInteger(item.index) || item.index < 0 || item.index > 11) {
    issues.push({ path:`${base}.index`, code:'invalid_cycle_index', message:'運限宮位索引必須介於 0～11。' });
  }
  if (!item.heavenlyStem.trim()) issues.push({ path:`${base}.heavenlyStem`, code:'missing_heavenly_stem', message:'缺少運限天干。' });
  if (!item.earthlyBranch.trim()) issues.push({ path:`${base}.earthlyBranch`, code:'missing_earthly_branch', message:'缺少運限地支。' });

  const mappings = Object.entries(item.cyclePalaceByNatalPalace);
  if (mappings.length !== 12) {
    issues.push({ path:`${base}.cyclePalaceByNatalPalace`, code:'incomplete_palace_map', message:'運限必須提供完整 12 宮映射。' });
  } else {
    const keys = new Set(mappings.map(([key])=>key));
    const values = new Set(mappings.map(([,value])=>value));
    if (PALACE_IDS.some((id)=>!keys.has(id))) issues.push({ path:`${base}.cyclePalaceByNatalPalace`, code:'missing_natal_palace', message:'運限宮位映射缺少本命宮位。' });
    if (values.size !== 12) issues.push({ path:`${base}.cyclePalaceByNatalPalace`, code:'duplicate_cycle_palace', message:'運限十二宮映射不得重複。' });
  }

  if (item.mutagens.length !== 4 || new Set(item.mutagens.map((entry)=>entry.mutagen)).size !== 4) {
    issues.push({ path:`${base}.mutagens`, code:'invalid_transformations', message:'此運限層必須保留祿、權、科、忌四化各一筆。' });
  }

  if (item.scope === 'minor') {
    if (!Number.isInteger(item.nominalAge) || (item.nominalAge ?? 0) < 1) {
      issues.push({ path:`${base}.nominalAge`, code:'missing_nominal_age', message:'小限必須保留可重現的虛歲。' });
    }
    if (Object.keys(item.movingStarsByNatalPalace).length !== 0) {
      issues.push({ path:`${base}.movingStarsByNatalPalace`, code:'minor_cycle_moving_stars_not_allowed', message:'小限不可偽裝成具有獨立流耀集合。' });
    }
  } else {
    const buckets = Object.entries(item.movingStarsByNatalPalace);
    if (buckets.length !== 12 || PALACE_IDS.some((id)=>!(id in item.movingStarsByNatalPalace))) {
      issues.push({ path:`${base}.movingStarsByNatalPalace`, code:'incomplete_moving_star_buckets', message:'大限／流年／流月／流日／流時必須保留 12 宮流耀 bucket，即使其中為空。' });
    }
  }
}

export function validateNormalizedHoroscopeContract(horoscope: NormalizedHoroscope): readonly ContractIssue[] {
  const issues: ContractIssue[] = [];
  if (!horoscope.natalChartFingerprint.trim()) issues.push({ path:'horoscope.natalChartFingerprint', code:'missing_natal_fingerprint', message:'運限必須綁定本命命盤 fingerprint。' });
  if (!horoscope.targetSolarDate.trim()) issues.push({ path:'horoscope.targetSolarDate', code:'missing_target_date', message:'運限必須指定目標陽曆日期。' });
  if (!Number.isInteger(horoscope.targetTimeIndex) || horoscope.targetTimeIndex < 0 || horoscope.targetTimeIndex > 12) {
    issues.push({ path:'horoscope.targetTimeIndex', code:'invalid_target_time', message:'目標時辰索引必須介於 0～12。' });
  }
  for (const scope of [...MOVING_SCOPES.slice(0,1), 'minor', ...MOVING_SCOPES.slice(1)] as const) validateItem(horoscope[scope], issues);
  return issues;
}

export function assertNormalizedHoroscopeContract(horoscope: NormalizedHoroscope): void {
  const issues = validateNormalizedHoroscopeContract(horoscope);
  if (issues.length) throw new Error(`Normalized horoscope contract failed: ${issues.map((issue)=>`${issue.path}:${issue.code}`).join(', ')}`);
}
